/**
 * The panel for whichever block is selected.
 *
 * Every block is edited here rather than on its card: a card shows what a step
 * does, the panel changes it. Which body appears is the only thing that varies
 * by step type.
 */

import React, { FC } from 'react';
import {
	AOP_MAX_DESCRIPTION,
	AOPCommunicateStep,
	AOPDecisionStep,
	AOPDraftStep,
	AOP_AFTER_EXIT,
	AOPSuggestion,
	AOPUnsupportedStep,
	AOPViolation,
	AOPFinishStep,
	AOPOperateStep,
	AOPOutcome,
	AOPParty,
	AOPScenario,
	AOPStep,
} from '../../../types/AOPTypes';
import { AOPSelect, AOPSelectOption, AOPTextArea, AOPTextField } from '../fields/AOPFields';
import {
	AOP_OPERATIONS,
	exitLabel,
	AOP_OUTCOMES,
	AOP_GROUP_BG_CLASS,
	AOP_PARTIES,
	accentBgClassForStep,
	outcomeDescription,
	stepNumberOf,
	stepTitle,
} from '../utils/AOPPlanUtils';
import { AOPSuggestionNote, AOPViolationNote, suggestionsFor, violationsFor } from '../AOPNotes';
import { AOPNotExecutable, AOPUnsupportedDetail } from '../AOPUnsupportedDetail';
import { AOPDecisionPanel } from './AOPDecisionPanel';
import { AOPPanelField, AOPPanelReadOnly, AOPPanelSection, AOPPanelShell } from './AOPPanelShell';
import { AOPStartPanel } from './AOPStartPanel';

const PARTY_OPTIONS: AOPSelectOption[] = AOP_PARTIES.map((party) => ({
	value: party.value,
	label: party.label,
}));

const OUTCOME_OPTIONS: AOPSelectOption[] = AOP_OUTCOMES.map((outcome) => ({
	value: outcome.value,
	label: outcome.label,
}));

/**
 * Every step as a destination, numbered the way the canvas numbers them.
 *
 * Shared by the two panels that point an exit somewhere, so a communicate step
 * and an operate step offer the same list in the same words.
 */
const targetOptionsOf = (steps: AOPDraftStep[]): AOPSelectOption[] =>
	steps.map((other, index) => ({
		value: other.name,
		label: `${stepNumberOf(index)} · ${stepTitle(other)}`,
	}));

const CommunicateBody: FC<{
	step: AOPCommunicateStep;
	steps: AOPDraftStep[];
	isReadOnly: boolean;
	onChange: (patch: Partial<AOPCommunicateStep>) => void;
}> = ({ step, steps, isReadOnly, onChange }) => {
	// Exactly one, named `after` — required upstream, so a step without it is a
	// stored plan we can still repair rather than refuse to draw.
	const after = step.exits && step.exits.length > 0 ? step.exits[0] : null;
	const connected = after ? steps.some((other) => other.name === after.target) : false;

	return (
		<>
			<AOPPanelField label="Who Radar contacts" required>
				<AOPSelect
					ariaLabel="Recipient"
					value={step.party}
					options={PARTY_OPTIONS}
					disabled={isReadOnly}
					onChange={(party) => onChange({ party: party as AOPParty })}
				/>
			</AOPPanelField>

			<AOPPanelField label="Note" required help="Radar leaves this as a note on the work order.">
				<AOPTextArea
					ariaLabel="Note"
					rows={5}
					value={step.message}
					disabled={isReadOnly}
					// Required upstream: an empty one takes the whole plan patch down and
					// comes back as a save that failed, naming nothing.
					validate={(message) =>
						message.trim().length === 0 ? 'Radar needs something to write in the note.' : null
					}
					onCommit={(message) => onChange({ message })}
				/>
			</AOPPanelField>

			{/*
		  Where it goes next, which used to be settable only by dragging the
		  fall-through dot on the canvas — so an author working in the panel could
		  read every field of this step and never see the one deciding what
		  follows it. A communicate step has only ever one successor, so this is a
		  single destination rather than the operate step's table of them.
		*/}
			<AOPPanelField
				label="Then go to"
				required
				help="Where Radar continues once the note is written."
			>
				<AOPSelect
					ariaLabel="Then go to"
					value={connected && after ? after.target : ''}
					placeholder="Not connected"
					options={targetOptionsOf(steps)}
					disabled={isReadOnly}
					className={connected ? '' : 'aop-select-invalid'}
					onChange={(target) => onChange({ exits: [{ name: AOP_AFTER_EXIT, target }] })}
				/>
			</AOPPanelField>
		</>
	);
};

const OperateBody: FC<{
	step: AOPOperateStep;
	steps: AOPDraftStep[];
	isReadOnly: boolean;
	onChange: (patch: Partial<AOPOperateStep>) => void;
}> = ({ step, steps, isReadOnly, onChange }) => {
	const operation = AOP_OPERATIONS[step.operation];
	const stepIds = steps.map((other) => other.name);
	const targetOptions = targetOptionsOf(steps);

	return (
		<>
			{/*
			  No assignment-mode control: the plan has one operation and no field
			  for a supplier list, so a "Custom list" option would be a promise
			  nothing can keep.
			*/}
			{/*
			  Guarded, like the card on the canvas: `operation` is a vocabulary the
			  service owns and adds to, so one we have never seen is a step we can
			  still show rather than a crash in the editor. Its own name is the
			  honest label when we have no words for it.
			*/}
			<AOPPanelField label="Assignment" help={operation ? operation.description : undefined}>
				<AOPPanelReadOnly>{operation ? operation.label : step.operation}</AOPPanelReadOnly>
			</AOPPanelField>

			<AOPPanelSection title="Outcomes" required>
				<div className="rounded border border-solid border-grey-4">
					<div className="flex items-center gap-m border-0 border-b border-solid border-grey-4 bg-grey-2 px-s py-xs">
						<span className="footnoteregular w-[160px] shrink-0 text-character-secondary">
							<span className="mr-2xs text-character-danger">*</span>If
						</span>
						<span className="footnoteregular flex-1 text-character-secondary">
							<span className="mr-2xs text-character-danger">*</span>Then go to
						</span>
					</div>

					{step.exits.map((exit, index) => {
						// A target naming no step is what the service reports as
						// `unresolved`; the picker says so rather than showing an id
						// that resolves to nothing.
						const connected = stepIds.indexOf(exit.target) >= 0;

						return (
							<div
								key={exit.name}
								className="flex items-center gap-m px-s py-xs [&:not(:last-child)]:border-0 [&:not(:last-child)]:border-b [&:not(:last-child)]:border-solid [&:not(:last-child)]:border-grey-4"
							>
								<span className="bodyregular w-[160px] shrink-0 text-character-primary">
									{exitLabel(exit.name)}
								</span>
								<div className="min-w-0 flex-1">
									<AOPSelect
										ariaLabel={`Where ${exit.name} goes`}
										value={connected ? exit.target : ''}
										placeholder="Not connected"
										options={targetOptions}
										disabled={isReadOnly}
										className={connected ? '' : 'aop-select-invalid'}
										onChange={(target) =>
											onChange({
												exits: step.exits.map((other, position) =>
													position === index ? { ...other, target } : other
												),
											})
										}
									/>
								</div>
							</div>
						);
					})}
				</div>

				<span className="footnoteregular text-character-secondary">
					The two outcomes are fixed by the operation. Point each one at the block it should
					continue at.
				</span>
			</AOPPanelSection>
		</>
	);
};

/**
 * What the author asked for that no term covers, and the ways out of it.
 *
 * The alternatives are whole steps the compiler wrote — swapping one in
 * replaces the unsupported step with it, keeping its id so anything pointing
 * here keeps pointing at the replacement.
 */
const UnsupportedBody: FC<{
	step: AOPUnsupportedStep;
	violations: AOPViolation[];
	isReadOnly: boolean;
	onReplace: (alternative: AOPStep) => void;
	onRemove: () => void;
}> = ({ step, violations, isReadOnly, onReplace, onRemove }) => (
	<>
		<AOPPanelField label="What you asked for">
			<AOPPanelReadOnly>{step.request}</AOPPanelReadOnly>
		</AOPPanelField>

		<AOPPanelSection title={<AOPNotExecutable />}>
			<AOPUnsupportedDetail
				step={step}
				violations={violations}
				isReadOnly={isReadOnly}
				onReplace={onReplace}
				onRemove={onRemove}
			/>
		</AOPPanelSection>
	</>
);

const FinishBody: FC<{
	step: AOPFinishStep;
	isReadOnly: boolean;
	onChange: (patch: Partial<AOPFinishStep>) => void;
}> = ({ step, isReadOnly, onChange }) => (
	<AOPPanelField label="Outcome" required help={outcomeDescription(step.outcome)}>
		<AOPSelect
			ariaLabel="Outcome"
			value={step.outcome}
			options={OUTCOME_OPTIONS}
			disabled={isReadOnly}
			onChange={(outcome) => onChange({ outcome: outcome as AOPOutcome })}
		/>
	</AOPPanelField>
);

interface AOPBlockPanelProps {
	/** The selected step, or null when the Start block is selected. */
	step: AOPDraftStep | null;
	/** Every step in the plan, in order — what a branch can be pointed at. */
	steps: AOPDraftStep[];
	/** Everything the service said; filtered to this step inside. */
	suggestions: AOPSuggestion[];
	violations: AOPViolation[];
	onAcceptSuggestion: (suggestionId: string) => void;
	isStart: boolean;
	isReadOnly: boolean;
	scenario: AOPScenario | null;
	scenariosHref: string;
	onChange: (patch: Partial<AOPDraftStep>) => void;
	/** Swaps the step whole, keeping its name. See `replaceStep` on the editor. */
	onReplaceStep: (next: AOPDraftStep) => void;
	onRemove: () => void;
	onClose: () => void;
	width: number;
	onResizePointerDown: (event: React.PointerEvent) => void;
	onResizeKeyDown: (event: React.KeyboardEvent) => void;
}

export const AOPBlockPanel: FC<AOPBlockPanelProps> = ({
	step,
	steps,
	suggestions,
	violations,
	onAcceptSuggestion,
	isStart,
	isReadOnly,
	scenario,
	scenariosHref,
	onChange,
	onReplaceStep,
	onRemove,
	onClose,
	width,
	onResizePointerDown,
	onResizeKeyDown,
}) => {
	// Read once here: the decision panel takes its own copies for the Question
	// tab, and every other kind of step shows them at the foot of the panel.
	//
	// `step` is null for the Start block, which is drawn but is not a step — so
	// this cannot reach for a name before the `isStart` branch below has had its
	// say.
	const stepViolations = step ? violationsFor(violations, step.name) : [];

	/**
	 * What the service will take as a block's name.
	 *
	 * Checked by the field, so the refusal belongs to the text rather than to a
	 * step id this panel has to keep in step with whatever is selected.
	 */
	const validateTitle = (description: string): string | null => {
		const trimmed = description.trim();

		if (trimmed.length === 0) {
			return 'A block needs a name.';
		}

		if (trimmed.length > AOP_MAX_DESCRIPTION) {
			return `${AOP_MAX_DESCRIPTION} characters at most — this one is ${trimmed.length}.`;
		}

		return null;
	};

	const openSuggestions = step
		? suggestionsFor(suggestions, step.name).filter((suggestion) => suggestion.status === 'open')
		: [];

	const resize = {
		width,
		onResizePointerDown,
		onResizeKeyDown,
	};

	if (isStart) {
		return (
			<AOPPanelShell
				title={<span className="h5medium">Start</span>}
				accentClass={AOP_GROUP_BG_CLASS.lifecycle}
				onClose={onClose}
				{...resize}
			>
				<AOPStartPanel scenario={scenario} scenariosHref={scenariosHref} />
			</AOPPanelShell>
		);
	}

	if (!step) {
		return null;
	}

	return (
		<AOPPanelShell
			accentClass={accentBgClassForStep(step.type)}
			onClose={onClose}
			{...resize}
			// A published version is immutable: it can be read here but not changed,
			// and removing a step from it is meaningless.
			onRemove={isReadOnly ? undefined : onRemove}
			title={
				// A decision is named by its question, which the Question tab edits —
				// renaming it here as well would be the same field in two places. The
				// other step types have no such field, so they are renamed here.
				isReadOnly || step.type === 'decision' ? (
					<span className="h5medium block text-character-primary">{stepTitle(step)}</span>
				) : (
					<AOPTextField
						ariaLabel="Block name"
						value={step.description}
						validate={validateTitle}
						onCommit={(description) => onChange({ description: description.trim() })}
						className="aop-panel-title"
					/>
				)
			}
		>
			{step.type === 'unsupported' ? (
				<UnsupportedBody
					step={step as AOPUnsupportedStep}
					violations={violationsFor(violations, step.name)}
					isReadOnly={isReadOnly}
					// Replaces rather than patches: `onChange` merges, which left the
					// unsupported step's `request`, `reason` and `alternatives` on a
					// shape that declares none of them, and the service rejected the
					// save. The name is kept, so every exit pointing here still lands.
					onReplace={onReplaceStep}
					onRemove={onRemove}
				/>
			) : null}

			{step.type === 'decision' ? (
				<AOPDecisionPanel
					step={step as AOPDecisionStep}
					steps={steps}
					suggestions={suggestionsFor(suggestions, step.name)}
					violations={violationsFor(violations, step.name)}
					onAcceptSuggestion={onAcceptSuggestion}
					isReadOnly={isReadOnly}
					onChange={onChange}
				/>
			) : null}
			{step.type === 'communicate' ? (
				/*
				  Keyed to the step. The refusal lives in the field now, which is
				  keyed by the text it holds, so this is no longer what prevents one
				  block's complaint appearing over another's — but a panel body that
				  is reused across two different steps is a trap either way.
				*/
				<CommunicateBody
					key={step.name}
					step={step as AOPCommunicateStep}
					steps={steps}
					isReadOnly={isReadOnly}
					onChange={onChange}
				/>
			) : null}
			{step.type === 'operate' ? (
				<OperateBody
					step={step as AOPOperateStep}
					steps={steps}
					isReadOnly={isReadOnly}
					onChange={onChange}
				/>
			) : null}
			{step.type === 'finish' ? (
				<FinishBody step={step as AOPFinishStep} isReadOnly={isReadOnly} onChange={onChange} />
			) : null}

			{/*
			  Findings for every kind of step but a decision, which shows its own on
			  the Question tab.

			  An open suggestion blocks a publish whatever step it is anchored to, so
			  a block with no way to rule on one is a procedure that cannot be
			  published at all — the badge said something needed attention and the
			  panel offered nothing to do about it.
			*/}
			{step.type === 'decision' ||
			(stepViolations.length === 0 && openSuggestions.length === 0) ? null : (
				<div className="flex flex-col gap-xs">
					{stepViolations.map((violation, index) => (
						<AOPViolationNote key={index} violation={violation} />
					))}
					{openSuggestions.map((suggestion) => (
						<AOPSuggestionNote
							key={suggestion.id}
							suggestion={suggestion}
							onAccept={isReadOnly ? undefined : () => onAcceptSuggestion(suggestion.id)}
						/>
					))}
				</div>
			)}
		</AOPPanelShell>
	);
};

export default AOPBlockPanel;
